import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../api/client';
import { Button, StatusBadge } from './common';
import { CreateTodoModal } from './modals';
import { getUserName } from '../utils/formatters';

/**
 * TopicDiscussion - Discussion interface during active meeting session
 */
const TopicDiscussion = ({ topic, session, users, meeting, onClose, onCloseTopic }) => {
  const [discussionNotes, setDiscussionNotes] = useState(topic.discussionNotes || '');
  const [todos, setTodos] = useState([]);
  const [showTodoModal, setShowTodoModal] = useState(false);
  const [editTodo, setEditTodo] = useState(null);

  useEffect(() => {
    loadTodos();
  }, [topic.id]);

  // Auto-save discussion notes with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      saveDiscussionNotes();
    }, 500);
    return () => clearTimeout(timer);
  }, [discussionNotes]);

  const loadTodos = async () => {
    try {
      const data = await api.get(`/todos/topic/${topic.id}`);
      setTodos(data);
    } catch (error) {
      console.error('Failed to load todos:', error);
    }
  };

  const saveDiscussionNotes = async () => {
    try {
      await api.patch(`/topics/${topic.id}/notes`, { discussionNotes });
    } catch (error) {
      console.error('Failed to save notes:', error);
    }
  };

  const handleTodoSave = () => {
    setShowTodoModal(false);
    loadTodos();
  };

  const handleEditSave = () => {
    setEditTodo(null);
    loadTodos();
  };

  return (
    <div style={{ padding: '2rem 2.5rem', minHeight: '100vh', background: 'var(--color-bg)' }}>
      <h1 style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--color-text)', marginBottom: '1.25rem' }}>
        Topic Discussion
      </h1>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <Button variant="secondary" onClick={onClose}>
          ← Back to Topics
        </Button>
        <Button variant="danger" onClick={onCloseTopic}>
          Close Topic
        </Button>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '1.4rem', fontWeight: '700', color: 'var(--color-text)', marginBottom: 0 }}>
          {topic.title}
        </h2>
      </div>

      {topic.description && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 className="card-title">Description</h3>
          <div className="topic-description" dangerouslySetInnerHTML={{ __html: topic.description }} />
        </div>
      )}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <h3 className="card-title" style={{ marginBottom: '0.5rem' }}>
          Discussion Notes
        </h3>
        <textarea
          className="form-input form-textarea"
          value={discussionNotes}
          onChange={(e) => setDiscussionNotes(e.target.value)}
          placeholder="Add notes about this discussion here..."
          rows="8"
          style={{ width: '100%' }}
        />
        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>
          Notes are auto-saved as you type
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 className="card-title" style={{ marginBottom: 0 }}>
            Task Items
          </h3>
          <Button variant="secondary" size="small" onClick={() => setShowTodoModal(true)}>
            Add Task
          </Button>
        </div>

        {todos.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">✓</div>
            <p>No task items yet</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Task</th>
                <th>Assigned To</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {todos.map(todo => (
                <tr key={todo.id} style={{ cursor: 'pointer' }} onClick={() => setEditTodo(todo)}>
                  <td style={{ fontWeight: '500' }}>{todo.title}</td>
                  <td style={{ fontSize: '0.9rem' }}>{todo.assignees.map(id => getUserName(id, users)).join(', ') || '—'}</td>
                  <td><StatusBadge status={todo.status} type="todo" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showTodoModal && (
        <CreateTodoModal
          topicId={topic.id}
          users={users}
          onClose={() => setShowTodoModal(false)}
          onSave={handleTodoSave}
        />
      )}

      {editTodo && (
        <CreateTodoModal
          topicId={topic.id}
          todo={editTodo}
          users={users}
          onClose={() => setEditTodo(null)}
          onSave={handleEditSave}
        />
      )}
    </div>
  );
};

TopicDiscussion.propTypes = {
  topic: PropTypes.object.isRequired,
  session: PropTypes.object.isRequired,
  users: PropTypes.array.isRequired,
  meeting: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onCloseTopic: PropTypes.func.isRequired,
};

export default TopicDiscussion;
